import { useEffect, useState } from "react";
import { useActions, useChangeBooleans, useFetchedValues } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import Scrollable from "../../Shared/Edit/Scrollable";
import CreateActionDialog from "./CreateActionDialog/CreateActionDialog";
import ActionTableTreeList from "./TreeList/ActionTableTreeList";
import UpdateActionDialog from "./UpdateActionDialog/UpdateActionDialog";
import Skeleton from "../../Shared/Skeleton/Skeleton";
import { getAttributes } from "../../../API/Attribute/Attribute";
import { useTranslation } from "react-i18next";

function ActionTable() {

    const { t } = useTranslation("actions/action_grid", {
        useSuspense: false
    })

    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");

    const [state, dispatch] = useActions();
    const [fetchedValues, { updateActionTableFetchedValue }] = useFetchedValues();
    const [, setChangeProperty] = useChangeBooleans();

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const initialize = async () => {
            if (!state.isInitialized) {
                try {
                    await getEntityAttributes();
                    const orderedActions = selectedComponent.actions
                    dispatch.setActions(orderedActions);
                    setLoading(false);
                } catch (error) {
                    console.error('Error initializing entity attributes:', error);
                }
            }
        };
        initialize();
    }, []);


    useEffect(() => {
        if (!loading) {
            dispatch.setActions(selectedComponent.actions)
        }
    }, [selectedComponent.actions])

    useEffect(() => {
        if (state.actions.length !== selectedComponent.actions.length) {
            setChangeProperty("isActionTableChanged", true);
        } else if (state.actions.length === selectedComponent.actions.length) {
            let isChanged = false;
            for (let actionIndex = 0; actionIndex < selectedComponent.actions.length; actionIndex++) {
                const defaultAction = selectedComponent.actions[actionIndex];
                const newAction = state.actions[actionIndex];
                debugger;
                // actions were reordered or show was changed
                if (isActionChanged(newAction, defaultAction)) {
                    isChanged = true;
                    break;
                }
                // action button group was changed
                if (
                    newAction.actionDisplay === "Button Group" && defaultAction.actionDisplay === "Button Group" &&
                    newAction.actions && defaultAction.actions
                ) {
                    if (newAction.actions.length !== defaultAction.actions.length) {
                        isChanged = true;
                    } else if (newAction.actions.length === defaultAction.actions.length) {
                        let childActionChanged = false;
                        for (let buttonIndex = 0; buttonIndex < defaultAction.actions.length; buttonIndex++) {
                            const defaultButton = defaultAction.actions[buttonIndex];
                            const newButton = newAction.actions[buttonIndex];
                            if (isActionChanged(newButton, defaultButton)) {
                                isChanged = true;
                                childActionChanged = true;
                                break;
                            }
                        }
                        if (childActionChanged) {
                            break;
                        }
                    }
                }
            }
            setChangeProperty("isActionTableChanged", isChanged);
        }
    }, [state.actions])

    function isActionChanged(newAction, defaultAction) {
        return (
            defaultAction.id !== newAction.id ||
            defaultAction.show !== newAction.show ||
            defaultAction.actionDisplay !== newAction.actionDisplay ||
            defaultAction.actionType !== newAction.actionType ||
            defaultAction.actionName !== newAction.actionName ||
            defaultAction.label !== newAction.label ||
            defaultAction.icon !== newAction.icon ||
            defaultAction.targetUrl !== newAction.targetUrl ||
            defaultAction.tooltip !== newAction.tooltip ||
            defaultAction.targetPageId !== newAction.targetPageId
        )
    }

    // functions 
    async function getEntityAttributes() {
        const attributes = await getAttributes(entityId);
        updateActionTableFetchedValue("attributes", attributes);
    }

    const getActionsOrdered = (actions) => {
        const sortRecursive = (items) => {
            if (!Array.isArray(items)) return items;
            items.sort((a, b) => (a.order || 0) - (b.order || 0));
            return items.map(item => {
                if (item.actions && Array.isArray(item.actions)) {
                    return {
                        ...item,
                        actions: sortRecursive(item.actions)
                    };
                }
                return item;
            });
        };
        return sortRecursive(actions);
    };

    function toggleCreateActionPopup() {
        dispatch.toggleCreateActionDialog();
    }

    if (loading) {
        return (
            <Skeleton
                rows={[
                    { columns: [{ size: 12 }] },
                    { full: true }
                ]}
            />
        )
    }

    return (
        <>
            {
                state.showCreateActionDialog &&
                <CreateActionDialog />
            }
            {
                state.showUpdateActionDialog &&
                <UpdateActionDialog />
            }
            <div className="d-flex align-items-center py-3 p-2 edit2">
                <div className="bigger flex-grow-1 ps-3">
                    {t("header.title")}
                </div>
                {accessedFromDesigner && (
                    <button
                        type="button"
                        className="btn btn-color"
                        onClick={toggleCreateActionPopup}
                        disabled={selectedComponent.hidden}
                    >
                        <i className="fas fa-plus mx-1"></i>{t("add_action")}
                    </button>
                )}
            </div>
            <Scrollable>
                <ActionTableTreeList />
            </Scrollable>
        </>
    )
}

export default ActionTable;